import { type FC, useMemo, useState } from "react";

import {
  isCatalogRecommendationTraitConfig,
  isFormulaTraitConfig,
  TraitType,
} from "@hightouch/lib/query/visual/types";
import {
  Badge,
  Button,
  Column,
  EyeIcon,
  InformationIcon,
  RedactedIcon,
  Row,
  Switch,
  Text,
  type TextProps,
  Tooltip,
  TextWithTooltip,
  type TextWithTooltipProps,
} from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { traitNeedsRedaction } from "@hightouch/lib/query/visual/trait-privacy";
import { decodeOrderedPreviewKey } from "@hightouch/lib/predictive-traits/audience-builder/catalog-recommendation-preview-keys";
import { isPresent } from "ts-extras";

import PlaceholderSrc from "src/assets/placeholders/generic.svg";
import { PermissionedButton } from "src/components/permission";
import { Table } from "src/ui/table";
import ErrorSrc from "src/assets/placeholders/error.png";
import { getColumnName } from "src/utils/models";

import { type PreviewableTrait, usePreviewTrait } from "./use-preview-trait";
import { useTraitSqlPreview } from "./use-trait-sql-preview";
import { TraitSqlPreviewDialog } from "./trait-sql-preview-dialog";
import type { ColumnFragment, FilterableColumn } from "src/graphql";
import type { TraitDefinition } from "src/types/visual";
import { transformModelColumnsForFormulaTrait } from "./utils";
import { stringify } from "src/utils/string";
import { isObjectLike, sampleSize } from "lodash";

type AudienceSubset = {
  subset_value: {
    id: string;
  };
};

type CatalogRecommendation = {
  item: unknown;
  rank: number;
};

type CatalogRecommendationUserRow = {
  id: string;
  primaryKey: unknown;
  recommendations: CatalogRecommendation[];
};

// A single recommended-item field rendered as its own preview column. For
// struct items the key is the catalog column name (e.g. "title", "price") and
// doubles as the column header; for scalar items there's a single synthetic
// column.
type CatalogItemColumn = { key: string | null; header: string };

const SCALAR_ITEM_HEADER = "Recommended item";

// The backend emits each recommended item as a struct keyed by order-prefixed
// catalog column names. Derive the field columns from an item, sorting by the
// order prefix (warehouses reorder JSON keys) and stripping it to recover the
// display header. Falls back to a single synthetic column for a bare scalar.
function getCatalogItemColumns(item: unknown): CatalogItemColumn[] {
  if (item != null && typeof item === "object" && !Array.isArray(item)) {
    return Object.keys(item as Record<string, unknown>)
      .map((key) => {
        const decoded = decodeOrderedPreviewKey(key);
        return {
          key,
          header: decoded?.name ?? key,
          order: decoded?.order ?? Number.MAX_SAFE_INTEGER,
        };
      })
      .sort((a, b) => a.order - b.order)
      .map(({ key, header }) => ({ key, header }));
  }
  return [{ key: null, header: SCALAR_ITEM_HEADER }];
}

function getCatalogItemValue(
  item: unknown,
  column: CatalogItemColumn,
): unknown {
  if (column.key == null) {
    return item;
  }
  return (item as Record<string, unknown> | null | undefined)?.[column.key];
}

type Props = {
  parentModel: {
    id: string;
    primary_key: string | null;
    columns: ColumnFragment[];
    traits: TraitDefinition[];
    filterable_audience_columns: FilterableColumn[];
    sampled_model_available: boolean;
    visual_query_primary_label: string | null;
    visual_query_secondary_label: string | null;
    semantic_type_name_column?: string | null;
    connection?: { id: string } | null;
    event?: {
      primary_key: string | null;
      timestamp_column: string | null;
    } | null;
  };
  trait: PreviewableTrait & { name?: string };
  subsets?: AudienceSubset[];
  isLoading?: boolean;
  isDisabled?: boolean;
  onValidateTrait?: () => Promise<void>;
  onError?: (error: Error) => void;
};

const NUM_PREVIEW_ROWS = 20;
const MAX_OBJECTS_PER_ROW = 3;
const MAX_CELL_WIDTH = 176;
const MAX_FORMULA_COLUMNS = 3;
const previewNoResultsMsg =
  "The preview did not return any results. Please try again.";
const parentModelNoDataMsg =
  "The preview did not return any results. Please ensure the parent model has data.";

export const PreviewTrait: FC<Props> = ({
  parentModel,
  trait,
  subsets,
  isLoading = false,
  isDisabled,
  onValidateTrait,
  onError,
}) => {
  const { enableTraitRedaction } = useFlags();
  const isSamplingAvailable = parentModel.sampled_model_available;
  const [isFastPreviewEnabled, setIsFastPreviewEnabled] =
    useState(isSamplingAvailable);

  const [isTraitRedacted, setIsTraitRedacted] = useState(false);

  const {
    previewTrait,
    data: traitPreviewData,
    error,
    isLoading: previewLoading,
    isSuccess: previewSucceeded,
  } = usePreviewTrait({
    parentModel: parentModel,
    trait,
    subsets,
    isFastPreviewEnabled,
  });

  const sqlPreview = useTraitSqlPreview({
    parentModel: {
      id: parentModel.id,
      connection: parentModel.connection,
    },
    trait,
    subsets,
    useSampledModels: isFastPreviewEnabled,
  });

  // Map the referenced aliases to the actual column name
  const formulaTraitColumns = useMemo<
    { columnName: string; alias: string }[]
  >(() => {
    // Bail if this isn't a formula trait
    if (!isFormulaTraitConfig(trait.type, trait.config)) {
      return [];
    }

    // Get all the columns in the correct format
    const allColumns = transformModelColumnsForFormulaTrait({
      columns: parentModel.filterable_audience_columns,
      traits: parentModel.traits,
    });

    return (trait.config.referencedColumns ?? [])
      .map(({ alias }) => {
        // Match against the variants list so legacy aliases hashed under an
        // alternate modelId shape still resolve to the live column.
        const column = allColumns.find((c) => c.aliases.includes(alias));
        return {
          // If we can't find the column, it's possible that it was deleted.
          // Fallback to the alias used in the referenced columns.
          // This is just for display purposes so the UI doesn't look broken either.
          columnName: column?.label ?? alias,
          alias: column?.alias ?? alias,
          // Only parent model columns appear as separate keys in the query
          // result. Related/merged/trait columns are consumed within the
          // formula SQL and won't have values in the row.
          isParent: column?.isParent ?? false,
        };
      })
      .filter(({ isParent }) => isParent);
  }, [parentModel, trait]);

  // Determine if we're previewing a trait on a related model
  // Related model traits won't have visual_query_primary_label or secondary configured

  // For related model and event model traits, determine appropriate labels.
  // `primaryKeyColumnName` is the row's unique identity (the parent model's
  // primary key, or the backend-inferred key for related-model traits) and is
  // distinct from the display label, which is optional and not unique.
  const {
    primaryKeyColumnName,
    primaryLabelColumnName,
    secondaryLabelColumnName,
  } = useMemo(() => {
    const isRelatedModelTrait = !parentModel.primary_key;
    // The backend infers the primary key, use this as the primary label
    if (isRelatedModelTrait && traitPreviewData?.inferredPrimaryKey) {
      // Use semantic type name column as secondary label if available
      // Event models will not have the semantic type name, but will have a timestamp
      // They also have an optional primary_key, which will be used instead of the inferred PK (from the join)
      const inferredPrimaryKey =
        parentModel.event?.primary_key ?? traitPreviewData.inferredPrimaryKey;
      return {
        primaryKeyColumnName: inferredPrimaryKey,
        primaryLabelColumnName: inferredPrimaryKey,
        secondaryLabelColumnName:
          parentModel.semantic_type_name_column ??
          parentModel.event?.timestamp_column ??
          "",
      };
    }

    // For parent model traits, use configured labels
    const primaryLabel = parentModel.visual_query_primary_label ?? "";
    const secondaryLabel = parentModel.visual_query_secondary_label ?? "";

    return {
      primaryKeyColumnName: parentModel.primary_key
        ? getColumnName(parentModel.primary_key, parentModel.columns)
        : null,
      primaryLabelColumnName: getColumnName(primaryLabel, parentModel.columns),
      secondaryLabelColumnName: getColumnName(
        secondaryLabel,
        parentModel.columns,
      ),
    };
  }, [traitPreviewData, parentModel]);

  const rows = traitPreviewData?.rows ?? [];
  const showTable = previewSucceeded || Boolean(error);
  const parentModelIsEmpty = previewSucceeded && rows.length === 0;

  // Catalog recommendation traits preview as a flat, expandable table (one row
  // per user with their ranked recommendations) rather than a single array
  // cell.
  const isCatalogRecommendationPreview = isCatalogRecommendationTraitConfig(
    trait.type,
    trait.config,
  );

  // List, catalog recommendation, and tracked-audience-membership traits render
  // an array in the preview cell, which reads better left-aligned than the
  // right-aligned scalar traits.
  const isListLikePreview =
    trait.type === TraitType.List ||
    trait.type === TraitType.TrackedAudienceMembership ||
    isCatalogRecommendationPreview;

  // The backend returns each user's recommended items ordered by ascending
  // rank and sliced to the predictor's top-K, so an item's 1-based position in
  // the array is its rank. Sample a handful of users and keep each user's full
  // ranked list — the collapsed row shows the user's #1 recommendation and
  // expanding reveals all K. Sampling users (rather than dumping one user's
  // list) surfaces personalization across the audience. The sample is stable
  // for a given preview result (recomputed only when the query re-runs).
  //
  // `catalogItemColumns` are the per-item fields the backend returned (the
  // catalog model's display columns, e.g. title + price), rendered as their
  // own titled columns.
  const { catalogRecommendationRows, catalogItemColumns } = useMemo<{
    catalogRecommendationRows: CatalogRecommendationUserRow[];
    catalogItemColumns: CatalogItemColumn[];
  }>(() => {
    if (!isCatalogRecommendationPreview) {
      return { catalogRecommendationRows: [], catalogItemColumns: [] };
    }

    const userRows: CatalogRecommendationUserRow[] = [];
    let itemColumns: CatalogItemColumn[] = [];
    for (const row of rows as any[]) {
      const items = Array.isArray(row?.trait_preview) ? row.trait_preview : [];
      if (items.length === 0) {
        continue;
      }
      if (itemColumns.length === 0) {
        itemColumns = getCatalogItemColumns(items[0]);
      }
      const primaryKey = primaryKeyColumnName
        ? row?.[primaryKeyColumnName]
        : undefined;
      userRows.push({
        id: primaryKey != null ? String(primaryKey) : `user-${userRows.length}`,
        primaryKey,
        recommendations: items.map((item: unknown, index: number) => ({
          item,
          rank: index + 1,
        })),
      });
    }

    return {
      catalogRecommendationRows: sampleSize(userRows, NUM_PREVIEW_ROWS),
      catalogItemColumns: itemColumns,
    };
  }, [isCatalogRecommendationPreview, rows, primaryKeyColumnName]);

  const renderCatalogItemCell = (item: unknown, column: CatalogItemColumn) =>
    isTraitRedacted ? (
      <Badge variant="warning" svgIcon={RedactedIcon}>
        Redacted
      </Badge>
    ) : (
      <CellValue value={getCatalogItemValue(item, column)} />
    );

  // The collapsed row is a teaser: the user's key plus their #1 recommendation
  // (the primary label). The full primary/secondary breakdown lives in the
  // expandable sub-table.
  const primaryItemColumn = catalogItemColumns[0];
  const catalogRecommendationColumns = [
    {
      max: `${MAX_CELL_WIDTH}px`,
      header: () => (
        <TextWithTooltip color="text.secondary" textTransform="capitalize">
          {primaryKeyColumnName || "User"}
        </TextWithTooltip>
      ),
      cell: (data: any) => <CellValue value={data.primaryKey} />,
    },
    {
      header: () => (
        <TextWithTooltip color="text.secondary">
          Top recommendation
        </TextWithTooltip>
      ),
      cell: (data: any) =>
        primaryItemColumn
          ? renderCatalogItemCell(
              data.recommendations[0]?.item,
              primaryItemColumn,
            )
          : null,
      cellSx: { bg: "primary.background !important" },
      headerSx: { bg: "primary.background" },
    },
  ];

  // Expanding a user reveals their full ranked recommendation list as a
  // sub-table with the same titled item columns plus a Rank column.
  const renderCatalogRecommendationExpandedRow = (
    row: CatalogRecommendationUserRow,
  ) => (
    <Table
      primaryKey="rank"
      width="100%"
      columns={[
        {
          min: "72px",
          max: "96px",
          header: () => (
            <Row width="100%" justifyContent="right">
              <TextWithTooltip color="text.secondary">Rank</TextWithTooltip>
            </Row>
          ),
          cell: (rec: any) => (
            <Row width="100%" justifyContent="right">
              <CellValue value={rec.rank} />
            </Row>
          ),
        },
        ...catalogItemColumns.map((column) => ({
          header: () => (
            <TextWithTooltip color="text.secondary" textTransform="capitalize">
              {column.header}
            </TextWithTooltip>
          ),
          cell: (rec: any) => renderCatalogItemCell(rec.item, column),
        })),
      ]}
      data={row.recommendations as any}
    />
  );

  const checkShouldRedactTrait = () => {
    if (!enableTraitRedaction) {
      return;
    }

    const traitRelationship =
      "relationshipId" in trait
        ? parentModel.traits.find(
            (t) => String(t.relationship?.id) === String(trait.relationshipId),
          )?.relationship
        : undefined;
    setIsTraitRedacted(
      traitNeedsRedaction(
        {
          type: trait.type,
          config: trait.config,
          relationship: traitRelationship,
        },
        parentModel,
      ),
    );
  };

  const clickPreview = async () => {
    try {
      await onValidateTrait?.();
      await previewTrait();
      checkShouldRedactTrait();
    } catch (error) {
      onError?.(error);
    }
  };

  return (
    <Column gap={2} align="flex-start" width="100%">
      <Row gap={3}>
        <PermissionedButton
          permission={{
            v2: {
              resource: "model",
              grant: "can_preview",
              id: parentModel.id,
            },
          }}
          icon={EyeIcon}
          isDisabled={isDisabled}
          isLoading={isLoading || previewLoading}
          onClick={clickPreview}
        >
          Preview results
        </PermissionedButton>
        <Button
          variant="secondary"
          isDisabled={isDisabled}
          onClick={sqlPreview.showSql}
        >
          View SQL
        </Button>
        {isSamplingAvailable && (
          <Row alignItems="center" gap={2}>
            <Row gap={1}>
              <Text fontWeight="medium">Fast preview</Text>
              <Tooltip message="Fast preview uses the sampled version of your models to speed up the query.">
                <InformationIcon />
              </Tooltip>
            </Row>
            <Switch
              aria-label="Enable fast preview."
              size="sm"
              isChecked={isFastPreviewEnabled}
              onChange={setIsFastPreviewEnabled}
            />
          </Row>
        )}
      </Row>
      <TraitSqlPreviewDialog
        sql={sqlPreview.sql}
        loading={sqlPreview.loading}
        visible={sqlPreview.visible}
        onClose={sqlPreview.hideSql}
        onCopy={sqlPreview.copySql}
      />
      {showTable && (
        <Column gap={2} width="100%">
          <Table
            scrollableX
            defaultMin="min-content"
            width="100%"
            primaryKey="id"
            renderExpandedRow={
              isCatalogRecommendationPreview
                ? (row) =>
                    renderCatalogRecommendationExpandedRow(
                      row as CatalogRecommendationUserRow,
                    )
                : undefined
            }
            columns={
              isCatalogRecommendationPreview
                ? catalogRecommendationColumns
                : [
                    // Only show primary label column if it exists
                    ...(primaryLabelColumnName
                      ? [
                          {
                            max: `${MAX_CELL_WIDTH}px`,
                            header: () => (
                              <TextWithTooltip
                                color="text.secondary"
                                textTransform="capitalize"
                              >
                                {primaryLabelColumnName}
                              </TextWithTooltip>
                            ),
                            cell: (data: any) => (
                              <CellValue value={data[primaryLabelColumnName]} />
                            ),
                          },
                        ]
                      : []),
                    // Only show secondary label column if it exists
                    ...(secondaryLabelColumnName
                      ? [
                          {
                            max: `${MAX_CELL_WIDTH}px`,
                            header: () => (
                              <TextWithTooltip
                                color="text.secondary"
                                textTransform="capitalize"
                              >
                                {secondaryLabelColumnName}
                              </TextWithTooltip>
                            ),
                            cell: (data: any) => (
                              <CellValue
                                value={data[secondaryLabelColumnName]}
                              />
                            ),
                          },
                        ]
                      : []),
                    // Show the values of the columns referenced in a formula trait,
                    // up to MAX_FORMULA_COLUMNS.
                    ...formulaTraitColumns
                      .filter(
                        ({ columnName }) =>
                          // We already show the primary and secondary label columns.
                          // So if they're used in the formula, we don't want to show them twice.
                          columnName !== primaryLabelColumnName &&
                          columnName !== secondaryLabelColumnName,
                      )
                      .slice(0, MAX_FORMULA_COLUMNS)
                      .map(({ alias, columnName }) => ({
                        max: `${MAX_CELL_WIDTH}px`,
                        header: () => (
                          <TextWithTooltip
                            color="text.secondary"
                            textTransform="capitalize"
                          >
                            {columnName}
                          </TextWithTooltip>
                        ),
                        cell: (row) => <CellValue value={row[alias]} />,
                      })),
                    {
                      header: () => (
                        <Row
                          width="100%"
                          justifyContent={isListLikePreview ? "left" : "right"}
                        >
                          <TextWithTooltip color="text.secondary">
                            {trait.name || "Trait preview"}
                          </TextWithTooltip>
                        </Row>
                      ),
                      cell: ({ trait_preview }) =>
                        isTraitRedacted ? (
                          <Badge variant="warning" svgIcon={RedactedIcon}>
                            Redacted
                          </Badge>
                        ) : (
                          <CellValue value={trait_preview} />
                        ),
                      cellSx: {
                        bg: "primary.background !important",
                        justifyContent: isListLikePreview ? "left" : "right",
                      },
                      headerSx: { bg: "primary.background" },
                    },
                  ]
            }
            data={
              (isCatalogRecommendationPreview
                ? catalogRecommendationRows
                : rows
              ).slice(0, NUM_PREVIEW_ROWS) as any
            }
            placeholder={{
              image: parentModelIsEmpty ? ErrorSrc : PlaceholderSrc,
              title: "No results",
              body: parentModelIsEmpty
                ? parentModelNoDataMsg
                : previewNoResultsMsg,
              error,
            }}
            error={Boolean(error)}
          />
        </Column>
      )}
    </Column>
  );
};

export const CellValue: FC<{ value: any } & Omit<TextProps, "children">> = ({
  value,
  ...props
}) => {
  if (!isPresent(value)) {
    return (
      <Text color="text.placeholder" {...props}>
        --
      </Text>
    );
  }

  let text = String(value);
  let textProps: Partial<TextWithTooltipProps> = {};

  if (
    Array.isArray(value) &&
    typeof value[0] === "object" &&
    value[0] != null
  ) {
    // Truncate text if too long
    text = `[\n${value
      .slice(0, MAX_OBJECTS_PER_ROW)
      .map((v) => "    " + stringify(v))
      .join(
        ",\n",
      )}${value.length > MAX_OBJECTS_PER_ROW ? "\n    ...," : ""}\n]`;

    if (value.length > MAX_OBJECTS_PER_ROW) {
      // Pass full message for tooltip, otherwise String() in TextWithTooltip truncates it
      textProps = {
        message: JSON.stringify(value),
        forceTooltip: true,
      };
    }
  } else if (isObjectLike(value)) {
    text = JSON.stringify(value);
  }

  return (
    <TextWithTooltip {...textProps} {...props}>
      {text}
    </TextWithTooltip>
  );
};
